import { type CSSProperties, type HTMLAttributes, type PropsWithChildren, useMemo } from 'react';
import { clsx } from 'clsx';
import type { Color } from '../../styles/tokens';
import {
  buildResponsiveProps,
  buildResponsiveVariableValue,
  type ResponsiveBoxSizing,
  type ResponsiveFlexBasis,
  type ResponsiveOverflow,
  type ResponsivePosition,
  type ResponsiveValue,
  type SpaceUnit,
} from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import styles from './box.module.scss';

const COMPONENT_STYLE_NAME = 'box';

export const DEFAULT_BOX_PADDING = {
  left: '16px',
  right: '16px',
  top: '16px',
  bottom: '16px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

export const NO_BOX_PADDING = {
  left: '0px',
  right: '0px',
  top: '0px',
  bottom: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

export const DEFAULT_BOX_PADDING_NO_VERTICAL = {
  ...DEFAULT_BOX_PADDING,
  top: '0px',
  bottom: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

export const DEFAULT_BOX_PADDING_NO_HORIZONTAL = {
  ...DEFAULT_BOX_PADDING,
  left: '0px',
  right: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

export interface BoxProps extends HTMLAttributes<HTMLDivElement> {
  backgroundColor?: Color;
  borderRadius?: ResponsiveValue<SpaceUnit>;
  flexBasis?: ResponsiveFlexBasis;
  maxWidth?: ResponsiveValue<SpaceUnit>;
  minWidth?: ResponsiveValue<SpaceUnit>;
  padding?: ResponsiveBoxSizing<SpaceUnit>;
  position?: ResponsivePosition;
  overflow?: ResponsiveOverflow;
}

export function Box({
  backgroundColor,
  borderRadius,
  children,
  className,
  flexBasis,
  maxWidth,
  minWidth,
  padding,
  position,
  style,
  overflow,
  ...props
}: PropsWithChildren<BoxProps>) {
  const baseStyle = useMemo(
    () =>
      sanitizeCSSVariables({
        [`--ixf-${COMPONENT_STYLE_NAME}-background-color`]: backgroundColor ? buildResponsiveVariableValue(backgroundColor, 'color') : 'none',
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'border-radius', borderRadius),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'flex', flexBasis),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'max-width', maxWidth),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'min-width', minWidth),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-top', padding?.top),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-right', padding?.right),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-bottom', padding?.bottom),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-left', padding?.left),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'position', position),
        ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'overflow', overflow),
        ...style,
      } as CSSProperties),
    [borderRadius, backgroundColor, flexBasis, maxWidth, minWidth, padding, position, overflow, style],
  );

  return (
    <div className={clsx(styles.box, className)} style={baseStyle} {...props}>
      {children}
    </div>
  );
}
